import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { derivedFullName, greetingName, hasRealName, nameAskLabel, splitConfirmedName } from '../workspace/userName';
import { btns } from './buttons';
import { curatedErrorMessage } from '../lib/curatedError';
import { hasVisibleContent } from '@steward-app/steward-types';
import { INVISIBLE_NAME_MESSAGE } from '../lib/nameCopy';

const DISMISS_KEY = 'steward-app:nameNudgeDismissed';

function readDismissed(): boolean {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
}

/** Mark the nudge dismissed from outside (the arrival overlay asks the same
 *  question — declining there shouldn't be re-asked here on the next screen). */
export function markNameNudgeDismissed(): void {
  try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private browsing */ }
}

/**
 * One-time, high-respect first impression: when the portal only knows the user by
 * their email-derived fallback name, ask "what should we call you?" instead of
 * greeting them by their email handle. Writes through PUT /me (same path as
 * Settings → Profile) and disappears once a real name is set. Dismissible — the
 * choice is remembered locally so it never nags.
 */
export function NameNudge() {
  const { profile, updateProfile } = useWorkspace();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Seed the field with the derived guess (e.g. "Matt Anderson") once the profile
  // is available, the same way WelcomeOverlay does (F13) — so this asks as a
  // confirmation rather than an empty box. A ref guards against re-seeding over
  // whatever the user has since typed, since `profile` can change reference again
  // after the save (at which point hasRealName flips true and this unmounts anyway).
  const prefilled = useRef(false);
  useEffect(() => {
    if (!profile || prefilled.current) return;
    prefilled.current = true;
    setValue(derivedFullName(profile));
  }, [profile]);

  // Wait for /me, skip when a real name is already known, and honor a prior
  // dismiss. Also stay hidden until the arrival overlay has been acknowledged
  // (welcomeSeenAt set): the overlay asks this same question first, and
  // showing both at once double-mounts two autoFocus name inputs. The dismiss
  // check re-reads localStorage at render time (not just mount-time state)
  // because the overlay writes the flag while this component is already
  // mounted — the overlay's own preference write triggers the re-render that
  // lands here, after the flag is set.
  if (!profile || !profile.preferences?.welcomeSeenAt || hasRealName(profile) || dismissed || readDismissed()) return null;

  function dismiss() {
    markNameNudgeDismissed();
    setDismissed(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const name = value.trim();
    if (!name) { setError('Please enter a name.'); return; }
    if (!hasVisibleContent(name)) { setError(INVISIBLE_NAME_MESSAGE); return; }
    setSaving(true); setError('');
    try {
      // Split, same as WelcomeOverlay.handleNameSubmit: givenName drives the short
      // greeting, name drives the account menu. Since this field is now prefilled
      // with the FULL derived guess (e.g. "Matt Anderson"), writing the whole
      // string to givenName too would greet a one-click accept in full everywhere.
      await updateProfile(splitConfirmedName(name));
      // No need to set dismissed — hasRealName(profile) is now true, so this unmounts.
    } catch (err) {
      // Default-deny (crossmarc/steward-app#2047): same `PUT /me` as Settings > Profile; the
      // server's `message` is never rendered.
      setError(curatedErrorMessage(err, {}, 'Could not save. Please try again.', 'Name nudge save'));
      setSaving(false);
    }
  }

  const greetName = greetingName(profile);

  return (
    <div className="relative mb-8 rounded-token border border-bronze/30 bg-surface p-5 shadow-panel">
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-3 top-3 text-slate hover:text-navy bg-transparent border-none cursor-pointer p-1"
      >
        <X size={16} strokeWidth={1.75} aria-hidden />
      </button>
      <h2 className="font-serif text-[17px] font-normal m-0 mb-1 text-navy">
        {greetName ? `Welcome, ${greetName}.` : 'Welcome.'}
      </h2>
      <label htmlFor="name-nudge-name" className="text-[13px] text-slate mb-4 max-w-md block">
        {nameAskLabel(profile)} You can change it anytime in Settings.
      </label>
      <form onSubmit={e => void handleSave(e)} className="flex flex-wrap items-start gap-2">
        <input
          id="name-nudge-name"
          autoFocus
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="Your name"
          maxLength={100}
          className="flex-1 min-w-[200px] px-3 py-2 text-[13px] border border-line-strong rounded-token box-border bg-surface text-navy font-sans outline-none focus:border-bronze-strong focus:ring-1 focus:ring-bronze-strong"
        />
        <button type="submit" className={btns.btnPrim} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className={btns.btn} onClick={dismiss} disabled={saving}>
          Not now
        </button>
      </form>
      {error && <div className="text-danger text-xs mt-2">{error}</div>}
    </div>
  );
}
